<script lang="ts">
  import { useForm, page, Link } from '@inertiajs/svelte';

  interface Props {
    errors?: {
      email_address?: string[];
      password?: string[];
      password_confirmation?: string[];
      invite_code?: string[];
      base?: string[];
    };
  }

  let { errors: propsErrors }: Props = $props();

  const form = useForm({
    user: {
      email_address: '',
      password: '',
      password_confirmation: '',
      invite_code: '',
    },
  });

  // Combine errors from props (server validation)
  let errors = $derived(propsErrors || {});

  function submit(e: Event) {
    e.preventDefault();
    $form.post('/registration');
  }
</script>

<div class="flex min-h-screen flex-col justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
  <div class="sm:mx-auto sm:w-full sm:max-w-md">
    <!-- Logo/Brand -->
    <div class="text-center">
      <h1 class="text-3xl font-bold text-gray-900">Momentium</h1>
      <p class="mt-2 text-sm text-gray-600">Plan your projects month by month</p>
    </div>

    <h2 class="mt-8 text-center text-2xl font-semibold text-gray-900">Create your account</h2>
  </div>

  <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
    <div class="border border-gray-100 bg-white px-4 py-8 shadow-lg sm:rounded-xl sm:px-10">
      <!-- Flash messages -->
      {#if $page.props.flash?.alert}
        <div class="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {$page.props.flash.alert}
        </div>
      {/if}

      <form onsubmit={submit} class="space-y-6">
        <!-- Email -->
        <div>
          <label for="email" class="block text-sm font-medium text-gray-700"> Email address </label>
          <input
            type="email"
            id="email"
            name="email_address"
            autocomplete="email"
            bind:value={$form.user.email_address}
            class="mt-1 w-full rounded-lg border px-3 py-2 transition-colors outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 {errors?.email_address
              ? 'border-red-300'
              : 'border-gray-300'}"
            required
          />
          {#if errors?.email_address}
            <p class="mt-1 text-sm text-red-600">{errors.email_address.join(', ')}</p>
          {/if}
        </div>

        <!-- Password -->
        <div>
          <label for="password" class="block text-sm font-medium text-gray-700"> Password </label>
          <input
            type="password"
            id="password"
            name="password"
            autocomplete="new-password"
            bind:value={$form.user.password}
            class="mt-1 w-full rounded-lg border px-3 py-2 transition-colors outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 {errors?.password
              ? 'border-red-300'
              : 'border-gray-300'}"
            required
            minlength="8"
          />
          {#if errors?.password}
            <p class="mt-1 text-sm text-red-600">{errors.password.join(', ')}</p>
          {:else}
            <p class="mt-1 text-xs text-gray-500">Minimum 8 characters</p>
          {/if}
        </div>

        <!-- Password Confirmation -->
        <div>
          <label for="password_confirmation" class="block text-sm font-medium text-gray-700">
            Confirm password
          </label>
          <input
            type="password"
            id="password_confirmation"
            name="password_confirmation"
            autocomplete="new-password"
            bind:value={$form.user.password_confirmation}
            class="mt-1 w-full rounded-lg border px-3 py-2 transition-colors outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 {errors?.password_confirmation
              ? 'border-red-300'
              : 'border-gray-300'}"
            required
          />
          {#if errors?.password_confirmation}
            <p class="mt-1 text-sm text-red-600">{errors.password_confirmation.join(', ')}</p>
          {/if}
        </div>

        <!-- Invite Code -->
        <div>
          <label for="invite_code" class="block text-sm font-medium text-gray-700">
            Invite code
          </label>
          <input
            type="text"
            id="invite_code"
            name="invite_code"
            autocomplete="off"
            bind:value={$form.user.invite_code}
            class="mt-1 w-full rounded-lg border px-3 py-2 transition-colors outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 {errors?.invite_code
              ? 'border-red-300'
              : 'border-gray-300'}"
            required
          />
          {#if errors?.invite_code}
            <p class="mt-1 text-sm text-red-600">{errors.invite_code.join(', ')}</p>
          {:else}
            <p class="mt-1 text-xs text-gray-500">Registration is invite-only during beta</p>
          {/if}
        </div>

        <!-- Base/rate limit errors -->
        {#if errors?.base}
          <div class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {errors.base.join(', ')}
          </div>
        {/if}

        <!-- Submit button -->
        <button
          type="submit"
          disabled={$form.processing}
          class="flex w-full justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          {#if $form.processing}
            <svg
              class="mr-2 -ml-1 h-4 w-4 animate-spin text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Creating account...
          {:else}
            Create account
          {/if}
        </button>
      </form>

      <!-- Sign in link -->
      <div class="mt-6">
        <div class="relative">
          <div class="absolute inset-0 flex items-center">
            <div class="w-full border-t border-gray-200"></div>
          </div>
          <div class="relative flex justify-center text-sm">
            <span class="bg-white px-2 text-gray-500">Already have an account?</span>
          </div>
        </div>

        <div class="mt-6">
          <Link
            href="/login"
            class="flex w-full justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
          >
            Sign in instead
          </Link>
        </div>
      </div>
    </div>
  </div>
</div>
